import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import { fetchData } from "../../api/client";
import { resolveApiErrorNotice } from "../ApiErrorAlertBody";
import ClientRelationshipBadge from "../Chat/ClientRelationshipBadge";
import { useClientDetails } from "../../hooks/useClientsDetails";
import { useClientRelationships } from "../../hooks/useClientRelationships";
import { useClientTags } from "../../hooks/useClientTags";
import { useAuthContext } from "../../context/AuthContext";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../hooks/useEffectivePermissions";
import {
  getClientRelationshipPeer,
  getClientRelationshipTypeLabel,
  getClientListRelationshipProps,
} from "../../utils/clientRelationships";
import { normalizeClientRequestForHistory } from "../../utils/clientRequestHistory";
import {
  formatPhoneAsYouType,
  formatStoredPhoneForInput,
  isPhoneSentinel,
  isUsableStoredPhone,
  toE164Digits,
} from "../../utils/phone";
import {
  getClientCommercialName,
  getClientLegalName,
  isPessoaJuridica,
} from "../../utils/clientName";
import { getSelectedCompanyIds } from "../../utils/workspaceStorage";

const ACTIVITY_LIMIT = 5;

const REQ_STATUS_MAP = {
  0: "Pendente",
  1: "Em atendimento",
  2: "Concluída",
  3: "Contato futuro",
  4: "Perdida",
  5: "Cancelada",
};

const looksLikeGroupRef = (value) => /grupo:|@g\.us|groupof:/i.test(String(value || ""));

const parseTags = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t || "").trim()).filter(Boolean);
  }
  return String(raw || "")
    .split(/[;,]/)
    .map((t) => t.trim())
    .filter(Boolean);
};

const tagsKey = (list) =>
  (Array.isArray(list) ? list : []).map((t) => String(t).trim()).filter(Boolean).join("\u0001");

const formatDate = (value) => {
  if (!value) return "";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleDateString();
};

const initialsFromName = (name) => {
  const parts = String(name || "")
    .split(" ")
    .filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const humanizeNotes = (raw, t) => {
  const text = String(raw || "").trim();
  if (!text) return { kind: "", text: "" };
  if (/unifica[cç][aã]o de cliente/i.test(text) || /"primary"\s*:/.test(text)) {
    const stamp = text.match(/\[(\d{4}-\d{2}-\d{2}[^\]\s]*)/);
    const when = stamp?.[1] ? formatDate(stamp[1].replace(" ", "T")) : "";
    return {
      kind: "merged",
      text: when
        ? t("pipelinePage.previewMergedOn", { date: when })
        : t("pipelinePage.previewMerged"),
    };
  }
  if (text.startsWith("{") && /resolvedFields|autoCopied/.test(text)) {
    return { kind: "", text: "" };
  }
  return { kind: "note", text };
};

const money = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "BRL" }).format(n);
};

const parseReqStatus = (status) => {
  if (status == null) return undefined;
  if (typeof status === "number" && Number.isFinite(status)) return status;
  const str = String(status).trim().toLowerCase();
  const n = Number(str);
  if (!Number.isNaN(n) && n in REQ_STATUS_MAP) return n;
  const found = Object.entries(REQ_STATUS_MAP).find(([, label]) => label.toLowerCase() === str);
  return found ? Number(found[0]) : undefined;
};

const reqStatusChipColor = (status) => {
  switch (parseReqStatus(status)) {
    case 0:
      return "warning";
    case 1:
      return "info";
    case 2:
      return "success";
    case 5:
      return "error";
    default:
      return "default";
  }
};

const openRequestPopup = (conversationId) => {
  const width = window.screen.width;
  const height = window.screen.height;
  window.open(
    `/popup-call/${conversationId}`,
    "SolicitaçãoPopup",
    `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
  );
};

const pickPhoneRaw = (client) => {
  const candidates = [client?.celular, client?.telefone, client?.whatsapp, client?.phone];
  return (
    candidates
      .map((v) => String(v || "").trim())
      .find((v) => v && !looksLikeGroupRef(v) && isUsableStoredPhone(v) && !isPhoneSentinel(v)) || ""
  );
};

const buildDraft = (client) => {
  const rawPhone = pickPhoneRaw(client);
  return {
    email: String(client?.email || "").trim(),
    phone: rawPhone ? formatStoredPhoneForInput(rawPhone) : "",
    cidade: String(client?.cidade || "").trim(),
    uf: String(client?.uf || "").trim().toUpperCase(),
    tags: parseTags(client?.tags),
    status: Number(client?.status) === 0 ? 0 : 1,
  };
};

const draftToClientPatch = (draft) => ({
  email: draft.email,
  celular: draft.phone ? toE164Digits(draft.phone) : "",
  cidade: draft.cidade,
  uf: draft.uf,
  tags: draft.tags.join(", "),
  status: draft.status,
});

const buildPayload = (draft, baseline) => {
  const payload = {};
  if (String(draft.email || "").trim() !== String(baseline.email || "").trim()) {
    payload.email = String(draft.email || "").trim();
  }
  if (String(draft.phone || "").trim() !== String(baseline.phone || "").trim()) {
    payload.celular = draft.phone ? toE164Digits(draft.phone) : "";
  }
  if (String(draft.cidade || "").trim() !== String(baseline.cidade || "").trim()) {
    payload.cidade = String(draft.cidade || "").trim();
  }
  if (String(draft.uf || "").trim() !== String(baseline.uf || "").trim()) {
    payload.uf = String(draft.uf || "").trim().toUpperCase();
  }
  if (tagsKey(draft.tags) !== tagsKey(baseline.tags)) {
    payload.tags = draft.tags.join(", ");
  }
  if (Number(draft.status) !== Number(baseline.status)) {
    payload.status = Number(draft.status);
  }
  return payload;
};

function ActivityRow({ title, subtitle, meta, chip, chipColor = "default", onClick }) {
  return (
    <Box
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        px: 1.5,
        py: 1.15,
        borderRadius: 1.5,
        cursor: onClick ? "pointer" : "default",
        transition: "background-color 120ms ease",
        "&:hover": onClick ? { bgcolor: "action.hover" } : undefined,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.4, wordBreak: "break-word" }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.15 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {chip ? (
          <Chip size="small" color={chipColor} variant="outlined" label={chip} sx={{ height: 22, fontWeight: 600 }} />
        ) : null}
        {meta ? (
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 600 }}>
            {meta}
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

export default function AwarenessClientPreviewDialog({
  open,
  client: seedClient,
  stageName = "",
  onClose,
  onClientUpdated,
}) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const canManage = can(scopeSetFromPermissions(permissionsData, userData), "clients", "manage");

  const clientId = seedClient?.id;
  const detailsId = open && clientId ? clientId : null;
  const [creatingKind, setCreatingKind] = useState("");
  const [actionError, setActionError] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [tagSearch, setTagSearch] = useState("");
  const [tab, setTab] = useState("");
  const [draft, setDraft] = useState(() => buildDraft(seedClient));
  const [baseline, setBaseline] = useState(() => buildDraft(seedClient));

  const draftRef = useRef(draft);
  const baselineRef = useRef(baseline);
  const dirtyRef = useRef(false);
  const clientIdRef = useRef(clientId);
  draftRef.current = draft;
  baselineRef.current = baseline;
  clientIdRef.current = clientId;
  dirtyRef.current = Object.keys(buildPayload(draft, baseline)).length > 0;

  const { data: details, isFetching: detailsFetching } = useClientDetails(detailsId);
  const { data: relationships = [], isFetching: relationshipsFetching } = useClientRelationships(detailsId);
  const { data: tagOptions } = useClientTags({ search: tagSearch, limit: 200, includeUsed: false });

  const client = useMemo(
    () => ({ ...(seedClient || {}), ...(details?.client || {}) }),
    [seedClient, details]
  );

  useEffect(() => {
    if (!open || !clientId) return;
    const next = buildDraft(seedClient);
    setDraft(next);
    setBaseline(next);
    dirtyRef.current = false;
    // Só reinicia o rascunho ao trocar de cliente — não quando o card é atualizado após o save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId]);

  useEffect(() => {
    if (!open || !clientId || dirtyRef.current || !details?.client) return;
    const next = buildDraft(details.client);
    setDraft(next);
    setBaseline(next);
  }, [open, clientId, details]);

  useEffect(() => {
    if (open) return;
    setCreatingKind("");
    setActionError(null);
    setSaveState("idle");
    setTab("");
    setTagSearch("");
    dirtyRef.current = false;
  }, [open]);

  const persist = useCallback(
    async (nextDraft, forId = clientIdRef.current) => {
      const current = nextDraft || draftRef.current;
      const payload = buildPayload(current, baselineRef.current);
      if (!forId || !Object.keys(payload).length) return;
      try {
        setSaveState("saving");
        setActionError(null);
        await fetchData(`/clients/${forId}`, "PUT", { id: forId, ...payload });
        if (forId !== clientIdRef.current) return;
        baselineRef.current = current;
        setBaseline(current);
        dirtyRef.current = false;
        onClientUpdated?.(draftToClientPatch(current));
        queryClient.invalidateQueries(["client-details", forId]);
        queryClient.invalidateQueries("clients");
        setSaveState("saved");
      } catch (error) {
        if (forId !== clientIdRef.current) return;
        const notice = resolveApiErrorNotice(error, t("pipelinePage.previewSaveError"));
        setActionError(typeof notice === "string" ? notice : notice?.message || String(notice));
        setSaveState("error");
      }
    },
    [onClientUpdated, queryClient, t]
  );

  const schedulePersist = useMemo(
    () => debounce((next) => persist(next), 700),
    [persist]
  );

  useEffect(() => () => schedulePersist.cancel(), [schedulePersist]);

  const patchDraft = (partial, { immediate = false } = {}) => {
    setDraft((prev) => {
      const next = { ...prev, ...partial };
      if (immediate) {
        schedulePersist.cancel();
        persist(next);
      } else {
        schedulePersist(next);
      }
      return next;
    });
  };

  const allowedTagNames = useMemo(() => {
    const catalog = (tagOptions || [])
      .filter((item) => (item?.status == null ? true : Number(item.status) === 1))
      .map((item) => String(item?.nome || "").trim())
      .filter(Boolean);
    const merged = new Set(catalog);
    draft.tags.forEach((tag) => merged.add(tag));
    return Array.from(merged);
  }, [draft.tags, tagOptions]);

  const displayName = getClientCommercialName(client) || t("pipelinePage.cardNoName");
  const legalName = getClientLegalName(client);
  const showLegalName =
    legalName && legalName.trim() && legalName.trim() !== String(displayName).trim();
  const isPj = isPessoaJuridica(client);
  const groupRef = [client.whatsapp, client.numero_whatsapp, client.celular, client.id_interno]
    .map((v) => String(v || "").trim())
    .find((v) => looksLikeGroupRef(v));
  const notes = humanizeNotes(client.observacao || client.observacoes, t);
  const seedRelationships = getClientListRelationshipProps(client);
  const relationshipList = Array.isArray(relationships) ? relationships : [];
  const relationshipCount = relationshipList.length || seedRelationships.count || 0;
  const relationshipHint = seedRelationships.hint;
  const isInactive = Number(draft.status) === 0;
  const registered = formatDate(client.data_cadastro);

  const requests = useMemo(
    () => (Array.isArray(details?.requests) ? details.requests : []).map(normalizeClientRequestForHistory),
    [details]
  );
  const purchases = Array.isArray(details?.purchases) ? details.purchases : [];
  const surveys = Array.isArray(details?.surveys) ? details.surveys : [];

  const tabs = useMemo(() => {
    const next = [];
    if (requests.length) {
      next.push({
        id: "requests",
        label: t("pipelinePage.previewRequestsTab"),
        count: requests.length,
        icon: <SupportAgentIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (purchases.length) {
      next.push({
        id: "sales",
        label: t("pipelinePage.previewSalesTab"),
        count: purchases.length,
        icon: <AddShoppingCartIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (surveys.length) {
      next.push({
        id: "surveys",
        label: t("pipelinePage.previewSurveysTab"),
        count: surveys.length,
        icon: <PollOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    if (relationshipList.length) {
      next.push({
        id: "relationships",
        label: t("pipelinePage.previewRelationshipsTab"),
        count: relationshipList.length,
        icon: <HubOutlinedIcon sx={{ fontSize: 16 }} />,
      });
    }
    return next;
  }, [purchases.length, relationshipList.length, requests.length, surveys.length, t]);

  useEffect(() => {
    if (!tabs.length) {
      if (tab) setTab("");
      return;
    }
    if (!tabs.some((item) => item.id === tab)) setTab(tabs[0].id);
  }, [tab, tabs]);

  const activityBusy = Boolean(detailsId) && (detailsFetching || relationshipsFetching) && !details;
  const showEmptyActivity = !activityBusy && !tabs.length;

  const openFullProfile = () => {
    if (!clientId) return;
    if (dirtyRef.current) {
      schedulePersist.cancel();
      persist(draftRef.current);
    }
    onClose?.();
    navigate(`/clients/${clientId}`);
  };

  const createRequest = async (typeKind) => {
    if (!clientId || creatingKind) return;
    try {
      setActionError(null);
      setCreatingKind(typeKind);
      const selected = getSelectedCompanyIds();
      const projectId = selected.length ? Number(selected[0]) : undefined;
      const created = await fetchData("/conversations", "POST", {
        projectId: Number.isFinite(projectId) ? projectId : undefined,
        clientId,
        client: { id: clientId },
        typeId: undefined,
        typeKind,
        initialMessage: "",
        source: "awareness-pipeline",
      });
      const conv = created?.data || created;
      if (!conv?.id) throw new Error(t("pipelinePage.previewCreateMissingId"));
      openRequestPopup(conv.id);
      queryClient.invalidateQueries(["client-details", clientId]);
    } catch (error) {
      const notice = resolveApiErrorNotice(
        error,
        typeKind === "2" ? t("pipelinePage.previewCreateSaleError") : t("pipelinePage.previewCreateRequestError")
      );
      setActionError(typeof notice === "string" ? notice : notice?.message || String(notice));
    } finally {
      setCreatingKind("");
    }
  };

  const handleTagsChange = (_event, next) => {
    const cleaned = (next || []).map((item) => String(item || "").trim()).filter(Boolean);
    const allowed = new Set(allowedTagNames.map((name) => name.toLowerCase()));
    const current = new Set(draft.tags.map((name) => name.toLowerCase()));
    const accepted = [];
    let rejected = false;
    cleaned.forEach((name) => {
      if (current.has(name.toLowerCase()) || !allowed.size || allowed.has(name.toLowerCase())) {
        if (!accepted.some((item) => item.toLowerCase() === name.toLowerCase())) accepted.push(name);
      } else {
        rejected = true;
      }
    });
    if (rejected) setActionError(t("pipelinePage.previewTagNotAllowed"));
    patchDraft({ tags: accepted }, { immediate: true });
  };

  const handleClose = () => {
    if (dirtyRef.current) {
      schedulePersist.cancel();
      persist(draftRef.current);
    }
    onClose?.();
  };

  const headerMeta = [client.id ? `#${client.id}` : "", stageName, registered ? t("pipelinePage.previewSince", { date: registered }) : ""]
    .filter(Boolean)
    .join("  ·  ");

  const renderActivity = () => {
    if (tab === "requests") {
      const items = requests.slice(0, ACTIVITY_LIMIT);
      return items.map((item) => (
        <ActivityRow
          key={item.id}
          title={`#${item.id}  ${item.subject || t("pipelinePage.previewRequestFallback")}`}
          subtitle={item.lastMessage && item.lastMessage !== item.subject ? item.lastMessage : ""}
          chip={REQ_STATUS_MAP[parseReqStatus(item.status)] || ""}
          chipColor={reqStatusChipColor(item.status)}
          meta={formatDate(item.timestamp || item.lastUpdate || item.date)}
          onClick={() => openRequestPopup(item.id)}
        />
      ));
    }
    if (tab === "sales") {
      return purchases.slice(0, ACTIVITY_LIMIT).map((item) => {
        const code = item.orderCode || item.codigo || `#${item.id}`;
        const product = item.product || item.items?.[0]?.name || item.items?.[0]?.nome || "";
        const amount = money(item.amount || item.total || item.subtotal);
        return (
          <ActivityRow
            key={item.id}
            title={[code, amount].filter(Boolean).join("  ·  ")}
            subtitle={product}
            meta={formatDate(item.date || item.data || item.createdAt)}
            onClick={() => {
              const saleId = item.pipelineId || item.saleId || item.id;
              if (!saleId) return;
              onClose?.();
              navigate(`/sales-pipeline/${saleId}`);
            }}
          />
        );
      });
    }
    if (tab === "surveys") {
      return surveys.slice(0, ACTIVITY_LIMIT).map((item) => {
        const defId = item.survey_id ?? item.surveyId ?? null;
        return (
          <ActivityRow
            key={item.id}
            title={item.title || t("pipelinePage.previewSurveyFallback")}
            chip={t("pipelinePage.previewSurveyAnswered")}
            chipColor="success"
            meta={formatDate(item.responseDate || item.date)}
            onClick={
              defId
                ? () => {
                    onClose?.();
                    navigate(`/surveys/${defId}/analytics`);
                  }
                : undefined
            }
          />
        );
      });
    }
    if (tab === "relationships") {
      return relationshipList.slice(0, ACTIVITY_LIMIT).map((rel) => {
        const peer = getClientRelationshipPeer(rel, clientId);
        if (!peer) return null;
        return (
          <ActivityRow
            key={rel.id}
            title={peer.nome || t("pipelinePage.previewRelatedFallback", { id: peer.clientId })}
            subtitle={getClientRelationshipTypeLabel(rel.relationship_type)}
            onClick={() => {
              onClose?.();
              navigate(`/clients/${peer.clientId}`);
            }}
          />
        );
      });
    }
    return null;
  };

  const activeCount = tabs.find((item) => item.id === tab)?.count || 0;
  const overflow = Math.max(0, activeCount - ACTIVITY_LIMIT);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="awareness-client-preview-title"
      PaperProps={{
        sx: {
          overflow: "hidden",
          borderRadius: 3,
          width: "100%",
          maxWidth: 840,
        },
      }}
    >
      <Box sx={{ height: 3, bgcolor: "primary.main" }} />
      <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 2.5, pb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            src={client?.imagem || undefined}
            sx={{
              width: 52,
              height: 52,
              fontWeight: 800,
              fontSize: "1.05rem",
              bgcolor: "primary.main",
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.22)}`,
            }}
          >
            {!client?.imagem ? initialsFromName(displayName) : null}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              id="awareness-client-preview-title"
              variant="h6"
              sx={{ fontWeight: 800, lineHeight: 1.25, letterSpacing: "-0.02em", wordBreak: "break-word" }}
            >
              {displayName}
            </Typography>
            {showLegalName ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.15, fontStyle: "italic" }}>
                {legalName}
              </Typography>
            ) : null}
            {headerMeta ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4, fontWeight: 600 }}>
                {headerMeta}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Chip
                size="small"
                color={isPj ? "primary" : "default"}
                variant={isPj ? "filled" : "outlined"}
                label={isPj ? t("pipelinePage.previewTypePj") : t("pipelinePage.previewTypePf")}
                sx={{ fontWeight: 700, height: 24 }}
              />
              <Chip
                size="small"
                color={isInactive ? "default" : "success"}
                variant="outlined"
                clickable={canManage}
                onClick={
                  canManage
                    ? () => patchDraft({ status: isInactive ? 1 : 0 }, { immediate: true })
                    : undefined
                }
                label={isInactive ? t("pipelinePage.cardInactive") : t("pipelinePage.previewActive")}
                sx={{ fontWeight: 600, height: 24 }}
              />
              {relationshipCount > 0 ? (
                <ClientRelationshipBadge
                  count={relationshipCount}
                  hint={relationshipHint}
                  clientId={client.id}
                  sx={{ maxWidth: "100%" }}
                />
              ) : null}
            </Stack>
          </Box>
          <IconButton aria-label={t("pipelinePage.previewClose")} onClick={handleClose} size="small" sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 0, pb: 2.5 }}>
        {actionError ? (
          <Alert severity="error" onClose={() => setActionError(null)} sx={{ mb: 1.5, borderRadius: 2 }}>
            {actionError}
          </Alert>
        ) : null}

        {notes.kind === "merged" ? (
          <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
            {notes.text}
          </Alert>
        ) : null}

        {canManage && (!draft.phone || !draft.email) ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.45 }}>
            {t("pipelinePage.previewEditHint")}
          </Typography>
        ) : !canManage ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.45 }}>
            {t("pipelinePage.previewViewHint")}
          </Typography>
        ) : null}

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ fontWeight: 800, letterSpacing: 0.7, display: "block", mb: 1.25 }}
        >
          {t("pipelinePage.previewContactSection")}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) minmax(0, 1fr)",
              md: "minmax(0, 1.15fr) minmax(0, 1.35fr) minmax(0, 1.1fr) 88px",
            },
            gap: 1.5,
          }}
        >
          <TextField
            size="small"
            label={t("pipelinePage.previewPhone")}
            value={draft.phone}
            InputProps={{ readOnly: !canManage }}
            onChange={(event) =>
              patchDraft({
                phone: event.target.value ? formatPhoneAsYouType(event.target.value) : "",
              })
            }
          />
          <TextField
            size="small"
            type="email"
            label={t("pipelinePage.previewEmail")}
            value={draft.email}
            InputProps={{ readOnly: !canManage }}
            onChange={(event) => patchDraft({ email: event.target.value })}
          />
          <TextField
            size="small"
            label={t("pipelinePage.previewCity")}
            value={draft.cidade}
            InputProps={{ readOnly: !canManage }}
            onChange={(event) => patchDraft({ cidade: event.target.value })}
          />
          <TextField
            size="small"
            label={t("pipelinePage.previewUf")}
            value={draft.uf}
            InputProps={{ readOnly: !canManage }}
            onChange={(event) =>
              patchDraft({ uf: String(event.target.value || "").toUpperCase().slice(0, 2) })
            }
            inputProps={{ maxLength: 2 }}
          />
        </Box>

        {groupRef ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
            <WhatsAppIcon sx={{ fontSize: 16, color: "#128C7E" }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t("pipelinePage.previewWhatsappGroup")}
            </Typography>
          </Stack>
        ) : null}

        {canManage || draft.tags.length ? (
          <Autocomplete
            multiple
            size="small"
            options={allowedTagNames}
            value={draft.tags}
            disabled={!canManage}
            onChange={handleTagsChange}
            onInputChange={(_event, value) => setTagSearch(value)}
            noOptionsText={t("pipelinePage.previewTagsEmpty")}
            sx={{ mt: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pipelinePage.previewTags")}
                placeholder={draft.tags.length ? "" : t("pipelinePage.previewTagsPlaceholder")}
              />
            )}
          />
        ) : null}

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75, minHeight: 20 }}>
          {saveState === "saving" ? (
            <>
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary">
                {t("pipelinePage.previewSaving")}
              </Typography>
            </>
          ) : null}
          {saveState === "saved" ? (
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
              {t("pipelinePage.previewSaved")}
            </Typography>
          ) : null}
        </Stack>

        {notes.kind === "note" ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
            {notes.text}
          </Typography>
        ) : null}

        <Divider sx={{ my: 2.25 }} />

        {activityBusy ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {t("pipelinePage.previewLoadingActivity")}
            </Typography>
          </Stack>
        ) : null}

        {showEmptyActivity ? (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {t("pipelinePage.previewNoActivity")}
          </Typography>
        ) : null}

        {tabs.length ? (
          <>
            <Tabs
              value={tab}
              onChange={(_event, value) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 40,
                mb: 0.5,
                "& .MuiTab-root": {
                  minHeight: 40,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  px: 1.25,
                },
              }}
            >
              {tabs.map((item) => (
                <Tab
                  key={item.id}
                  value={item.id}
                  icon={item.icon}
                  iconPosition="start"
                  label={`${item.label} · ${item.count}`}
                />
              ))}
            </Tabs>
            <Stack spacing={0.25}>{renderActivity()}</Stack>
            {overflow > 0 ? (
              <Button onClick={openFullProfile} size="small" sx={{ mt: 0.75, fontWeight: 700 }}>
                {t("pipelinePage.previewMoreItems", { count: overflow })}
              </Button>
            ) : null}
          </>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          py: 2,
          gap: 2,
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-end" },
          justifyContent: "space-between",
          bgcolor: alpha(theme.palette.background.default, 0.65),
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, letterSpacing: 0.3, display: "block", mb: 0.75 }}
          >
            {t("pipelinePage.previewFooterConsult")}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button onClick={handleClose} color="inherit" sx={{ fontWeight: 600 }}>
              {t("pipelinePage.previewClose")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={openFullProfile}
              disabled={!clientId}
              sx={{ fontWeight: 700 }}
            >
              {t("pipelinePage.previewOpenFull")}
            </Button>
          </Stack>
        </Box>
        <Box sx={{ minWidth: 0, textAlign: { sm: "right" } }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, letterSpacing: 0.3, display: "block", mb: 0.75 }}
          >
            {t("pipelinePage.previewFooterStart")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            useFlexGap
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={creatingKind === "1" ? <CircularProgress size={16} color="inherit" /> : <SupportAgentIcon />}
              onClick={() => createRequest("1")}
              disabled={!clientId || Boolean(creatingKind)}
              sx={{ fontWeight: 700 }}
            >
              {creatingKind === "1" ? t("pipelinePage.previewCreating") : t("pipelinePage.previewNewRequest")}
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={creatingKind === "2" ? <CircularProgress size={16} color="inherit" /> : <AddShoppingCartIcon />}
              onClick={() => createRequest("2")}
              disabled={!clientId || Boolean(creatingKind)}
              sx={{ fontWeight: 700 }}
            >
              {creatingKind === "2" ? t("pipelinePage.previewCreating") : t("pipelinePage.previewNewSale")}
            </Button>
          </Stack>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
